import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { commissionPlansApi, type CommissionPlan } from '@/api/commissionPlans';
import { useActionError } from '@/components/useActionError';
import { useToast } from '@/components/useToast';
import { commissionPlanErrorMessage } from './commissionPlanErrors';

/**
 * Copy a plan (TMMRSL-0049): no confirmation — copying only creates a draft (ACT-004). On success
 * the new draft opens; `navigate` pushes, so Back returns to the source. `copying` is the id in
 * flight: one copy at a time, whatever button started it.
 */
export function useCopyPlan(): {
  copy: (plan: Pick<CommissionPlan, 'id'>) => Promise<void>;
  copying: number | null;
} {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const showToast = useToast();
  const describe = useActionError(commissionPlanErrorMessage);
  const [copying, setCopying] = useState<number | null>(null);
  const inFlight = useRef(false);

  async function copy(plan: Pick<CommissionPlan, 'id'>) {
    if (inFlight.current) return;
    inFlight.current = true;
    setCopying(plan.id);
    try {
      const next = await commissionPlansApi.copyPlan(plan.id);
      showToast(t('commissionPlans.copied'));
      void navigate(`/commission-plans/${next.id}`);
    } catch (err) {
      showToast(describe(err).message, 'error');
    } finally {
      inFlight.current = false;
      setCopying(null);
    }
  }

  return { copy, copying };
}
